/*============================================================================*/

/**
 * Layout functions
 *
 * Functions that adjust the layout, spacing, and responsive
 * behavior of elements, including menus and other structural components.
 */

/*============================================================================*/

/*
=====================================
 SECTION 1: GLOBAL VARIABLES & CACHE
=====================================
*/

/**
 * Cache variables to prevent unnecessary DOM updates and layout thrashing.
 */
let _lastSpacingVal = -1;
let _lastIsDesktop = null;
let _lastOffsetVal = -1;
let _lastHeaderOffsetVal = -1;
let _lastFlexHeightVal = '';

/**
 * Request animation frame ID for layout updates to prevent multiple simultaneous calls.
 */
let _layoutUpdateRafId = null;

/**
 * Cache object storing previously applied menu positions to avoid redundant DOM updates.
 */
let _layoutCache = 
{
    upperTop: -9999,
    mainTop: -9999,
    factionTop: -9999,
    lowerTop: -9999,
    alertTop: -9999
};

/*
====================================
 SECTION 2: LAYOUT STATE MANAGEMENT
====================================
*/

/**
 * State management for layout elements and targets to optimize DOM access.
 * @type {Object}
 */
const LayoutState = 
{
    els: {},
    targets: {},
    heights: {},
    initialized: false,

    /**
     * Initializes DOM element caches to prevent layout thrashing during scroll events.
     */
    init: function() 
    {
        this.els = 
        {
            upper: document.getElementById('UpperMenuDesktopSection'),
            main: document.getElementById('MainHeaderMenu'),
            faction: document.getElementById('FactionCarouselController'),
            lower: document.getElementById('LowerMenuContainer'),
            alert: document.getElementById('AlertBarContainer')
        };
        
        this.targets = 
        {
            padAll: jQuery('.adjustPaddingFromTopMenus'),
            margAll: jQuery('.adjustMarginFromTopMenus'),
            padDesktop: jQuery('.d-adjustPaddingFromTopMenus'),
            margDesktop: jQuery('.d-adjustMarginFromTopMenus'),
            padMobile: jQuery('.m-adjustPaddingFromTopMenus'),
            margMobile: jQuery('.m-adjustMarginFromTopMenus'),
            belowMenuAll: jQuery('.belowMenu'),
            belowMenuDesktop: jQuery('.d-belowMenu'),
            belowMenuMobile: jQuery('.m-belowMenu'),
            stickyBelowMenuAll: jQuery('.stickyBelowMenu'),
            stickyBelowMenuDesktop: jQuery('.d-stickyBelowMenu'),
            stickyBelowMenuMobile: jQuery('.m-stickyBelowMenu'),
            belowHeaderAll: jQuery('.belowHeader'),
            belowHeaderDesktop: jQuery('.d-belowHeader'),
            belowHeaderMobile: jQuery('.m-belowHeader'),
            stickyBelowHeaderAll: jQuery('.stickyBelowHeader'),
            stickyBelowHeaderDesktop: jQuery('.d-stickyBelowHeader'),
            stickyBelowHeaderMobile: jQuery('.m-stickyBelowHeader'),
            flexAvailableAll: jQuery('.scrnhAvailable'),
            flexAvailableDesktop: jQuery('.d-scrnhAvailable'),
            flexAvailableMobile: jQuery('.m-scrnhAvailable')
        };
        
        this.updateHeights();

        if (window.ResizeObserver)
        {
            this.ro = new ResizeObserver(() => 
            {
                if (this.initialized)
                {
                    this.updateHeights();
                    updateLayout();
                }
            });
            
            Object.values(this.els).forEach(el => 
            {
                if (el)
                {
                    this.ro.observe(el);
                }
            });
        }

        this.initialized = true;
    },

    /**
     * Updates cached heights for all menu elements to prevent layout thrashing.
     */
    updateHeights: function()
    {
        this.heights = 
        {
            upper: this.els.upper ? this.els.upper.offsetHeight : 0,
            main: this.els.main ? this.els.main.offsetHeight : 0,
            faction: this.els.faction ? this.els.faction.offsetHeight : 0,
            lower: this.els.lower ? this.els.lower.offsetHeight : 0,
            alert: this.els.alert ? this.els.alert.offsetHeight : 0
        };
    },

    /**
     * Forces the state to rescan the DOM for dynamic elements.
     */
    refreshDynamicTargets: function()
    {
        if (this.initialized)
        {
            this.targets.belowMenuAll = jQuery('.belowMenu');
            this.targets.belowMenuDesktop = jQuery('.d-belowMenu');
            this.targets.belowMenuMobile = jQuery('.m-belowMenu');
        }
    }
};

/*
==============================
 SECTION 3: CORE CALCULATIONS
==============================
*/

/**
 * Calculates the absolute height of the header section including upper menu, main menu, and faction carousel.
 * 
 * @returns {number} The total height of all header elements in pixels.
 */
function getAbsoluteHeaderHeight()
{
    return LayoutState.heights.upper + LayoutState.heights.main + LayoutState.heights.faction;
}

/**
 * Calculates positions, heights, and visibility status for all fixed menu elements.
 * Utilizes a 1px overlap strategy to seal GPU anti-aliasing seams.
 *
 * @returns {Object} Object containing status, heights, positions, and boundary values.
 */
function calculateMenuPositions()
{
    if (!LayoutState.initialized)
    {
        LayoutState.init();
    }
    
    const els = LayoutState.els;
    const scrollTop = Math.max(0, window.scrollY || document.documentElement.scrollTop);
    const overlap = 1;

    const cachedH = LayoutState.heights;
    const h = 
    {
        upper: (els.upper && !els.upper.classList.contains('loading')) ? cachedH.upper : 0,
        main: cachedH.main,
        faction: cachedH.faction,
        lower: cachedH.lower,
        alert: cachedH.alert
    };

    const status = 
    {
        upper: h.upper > 0,
        main: h.main > 0,
        faction: h.faction > 0,
        lower: h.lower > 0,
        alert: h.alert > 0
    };

    const pos = { upper: 0, main: 0, faction: 0, lower: 0, alert: 0 };
    let upperVisibleH = 0;

    if (status.upper)
    {
        pos.upper = Math.round(Math.max(-h.upper, -scrollTop));
        upperVisibleH = Math.max(0, h.upper + pos.upper);
    }

    if (status.main)
    {
        pos.main = Math.round(status.upper && upperVisibleH > 0 ? upperVisibleH - overlap : 0);
        pos.main = Math.max(0, pos.main);
    }
    
    if (status.faction)
    {
        pos.faction = Math.round(pos.main + h.main - overlap);
    }

    if (status.lower)
    {
        const expandedY = pos.main + h.main + (status.faction ? h.faction : 0) - overlap;

        const hiddenY = status.faction ? pos.faction : pos.main;

        const scrollThreshold = 60; 
        const revealProgress = Math.min(1, scrollTop / scrollThreshold);

        pos.lower = Math.round(hiddenY + ((expandedY - hiddenY) * revealProgress));
    }
    else
    {
        pos.lower = 0;
    }
    
    if (status.alert)
    {
        pos.alert = Math.round(pos.main + h.main + h.faction + h.lower - overlap);
    }

    let maxBottom = 0;
    
    if (status.upper)
    {
        maxBottom = Math.max(maxBottom, pos.upper + h.upper);
    }
    if (status.main)
    {
        maxBottom = Math.max(maxBottom, pos.main + h.main);
    }
    if (status.faction)
    {
        maxBottom = Math.max(maxBottom, pos.faction + h.faction);
    }
    if (status.lower)
    {
        maxBottom = Math.max(maxBottom, pos.lower + h.lower);
    }
    if (status.alert)
    {
        maxBottom = Math.max(maxBottom, pos.alert + h.alert);
    }

    const flexOffset = status.main ? pos.main + h.main : 0;
    const headerBottom = status.main ? pos.main + h.main : 0;

    return { status, h, pos, upperVisibleH, maxBottom, headerBottom, flexOffset };
}

/*
=============================
 SECTION 4: DOM MANIPULATORS
=============================
*/

/**
 * Applies calculated top padding and margin to body elements.
 *
 * @param {number} val - The calculated spacing value in pixels.
 */
function applySpacing(val)
{
    const height = Math.max(0, val || 0);
    const isDesktop = window.innerWidth > 1000;

    if (Math.abs(_lastSpacingVal - height) < 0.5 && _lastIsDesktop === isDesktop)
    {
        return;
    }

    _lastSpacingVal = height;
    _lastIsDesktop = isDesktop;
    
    if (!LayoutState.initialized)
    {
        LayoutState.init();
    }
    
    const t = LayoutState.targets;

    t.padAll.css('padding-top', `${height}px`);
    t.margAll.css('margin-top', `${height}px`);

    if (isDesktop)
    {
        t.padDesktop.css('padding-top', `${height}px`);
        t.margDesktop.css('margin-top', `${height}px`);
        
        t.padMobile.css('padding-top', '');
        t.margMobile.css('margin-top', '');
    }
    else
    {
        t.padMobile.css('padding-top', `${height}px`);
        t.margMobile.css('margin-top', `${height}px`);
        
        t.padDesktop.css('padding-top', '');
        t.margDesktop.css('margin-top', '');
    }
}

/**
 * Adjusts the offset of elements positioned below the fixed menus using hardware acceleration.
 *
 * @param {number} maxBottom - The calculated top offset value for elements below ALL menus.
 * @param {number} [headerBottom=0] - The calculated top offset value for elements below MAIN menu only.
 * @param {boolean} [forceApply=false] - If true, bypasses cache to force DOM style update.
 */
function applyOffsetBelow(maxBottom, headerBottom = 0, forceApply = false)
{
    const topAll = Math.round(Math.max(0, maxBottom || 0));
    const topHeader = Math.round(Math.max(0, headerBottom || 0));
    const isDesktop = window.innerWidth > 1000;

    if (!forceApply && Math.abs(_lastOffsetVal - topAll) < 0.5 && Math.abs(_lastHeaderOffsetVal - topHeader) < 0.5 && _lastIsDesktop === isDesktop)
    {
        return;
    }
    
    _lastOffsetVal = topAll;
    _lastHeaderOffsetVal = topHeader;
    _lastIsDesktop = isDesktop;
    
    const transformStrAll = `translate3d(0, ${topAll}px, 0)`;
    const transformStrHeader = `translate3d(0, ${topHeader}px, 0)`;
    
    if (LayoutState.targets.belowMenuAll && LayoutState.targets.belowMenuAll.length)
    {
        LayoutState.targets.belowMenuAll.css('transform', transformStrAll);
    }
    
    if (isDesktop && LayoutState.targets.belowMenuDesktop && LayoutState.targets.belowMenuDesktop.length)
    {
        LayoutState.targets.belowMenuDesktop.css('transform', transformStrAll);
    }
    else if (!isDesktop && LayoutState.targets.belowMenuMobile && LayoutState.targets.belowMenuMobile.length)
    {
        LayoutState.targets.belowMenuMobile.css('transform', transformStrAll);
    }
    
    if (LayoutState.targets.belowHeaderAll && LayoutState.targets.belowHeaderAll.length)
    {
        LayoutState.targets.belowHeaderAll.css('transform', transformStrHeader);
    }
    
    if (isDesktop && LayoutState.targets.belowHeaderDesktop && LayoutState.targets.belowHeaderDesktop.length)
    {
        LayoutState.targets.belowHeaderDesktop.css('transform', transformStrHeader);
    }
    else if (!isDesktop && LayoutState.targets.belowHeaderMobile && LayoutState.targets.belowHeaderMobile.length)
    {
        LayoutState.targets.belowHeaderMobile.css('transform', transformStrHeader);
    }
    
    if (LayoutState.targets.stickyBelowMenuAll && LayoutState.targets.stickyBelowMenuAll.length)
    {
        LayoutState.targets.stickyBelowMenuAll.css('top', topAll + 'px');
    }
    
    if (isDesktop && LayoutState.targets.stickyBelowMenuDesktop && LayoutState.targets.stickyBelowMenuDesktop.length)
    {
        LayoutState.targets.stickyBelowMenuDesktop.css('top', topAll + 'px');
    }
    else if (!isDesktop && LayoutState.targets.stickyBelowMenuMobile && LayoutState.targets.stickyBelowMenuMobile.length)
    {
        LayoutState.targets.stickyBelowMenuMobile.css('top', topAll + 'px');
    }
    
    if (LayoutState.targets.stickyBelowHeaderAll && LayoutState.targets.stickyBelowHeaderAll.length)
    {
        LayoutState.targets.stickyBelowHeaderAll.css('top', topHeader + 'px');
    }
    
    if (isDesktop && LayoutState.targets.stickyBelowHeaderDesktop && LayoutState.targets.stickyBelowHeaderDesktop.length)
    {
        LayoutState.targets.stickyBelowHeaderDesktop.css('top', topHeader + 'px');
    }
    else if (!isDesktop && LayoutState.targets.stickyBelowHeaderMobile && LayoutState.targets.stickyBelowHeaderMobile.length)
    {
        LayoutState.targets.stickyBelowHeaderMobile.css('top', topHeader + 'px');
    }
}

/**
 * Applies calculated positions to menu elements using hardware-accelerated 3D transforms.
 *
 * @param {Object} layoutData - The calculation results from calculateMenuPositions.
 * @param {Object} cache - The cache object storing previously applied positions.
 */
function applyMenuPositions(layoutData, cache)
{
    const { status, pos, upperVisibleH } = layoutData;
    const els = LayoutState.els;

    if (status.upper && cache.upperTop !== pos.upper)
    {
        if (cache.upperTop === -9999)
        {
            els.upper.style.top = '0px';
        }
        
        els.upper.style.transform = `translate3d(0, ${pos.upper}px, 0)`;
        cache.upperTop = pos.upper;

        const isHidden = upperVisibleH <= 0;
        
        if (isHidden && !els.upper.classList.contains('hidden'))
        {
            els.upper.classList.add('hidden');
        }
        else if (!isHidden && els.upper.classList.contains('hidden'))
        {
            els.upper.classList.remove('hidden');
        }
    }

    if (status.main && cache.mainTop !== pos.main)
    {
        if (cache.mainTop === -9999)
        {
            els.main.style.top = '0px';
        }
        
        els.main.style.transform = `translate3d(0, ${pos.main}px, 0)`;
        cache.mainTop = pos.main;
    }

    if (status.faction && cache.factionTop !== pos.faction)
    {
        if (cache.factionTop === -9999)
        {
            els.faction.style.top = '0px';
        }
        
        els.faction.style.transform = `translate3d(0, ${pos.faction}px, 0)`;
        cache.factionTop = pos.faction;
    }

    if (status.lower && cache.lowerTop !== pos.lower)
    {
        if (cache.lowerTop === -9999)
        {
            els.lower.style.top = '0px';
        }
        
        els.lower.style.transform = `translate3d(0, ${pos.lower}px, 0)`;
        cache.lowerTop = pos.lower;
    }

    if (status.alert && cache.alertTop !== pos.alert)
    {
        if (cache.alertTop === -9999)
        {
            els.alert.style.top = '0px';
        }
        
        els.alert.style.transform = `translate3d(0, ${pos.alert}px, 0)`;
        cache.alertTop = pos.alert;
    }
}

/**
 * Resizes the flexible content area to fill the remaining viewport height.
 *
 * @param {number} [offsetPx=0] - Pixels to subtract from 100dvh.
 */
function applyFlexResize(offsetPx = 0)
{
    if (isNaN(offsetPx) || offsetPx === null)
    {
        offsetPx = 0;
    }
    const safeOffset = Math.floor(Math.max(0, offsetPx));
    const isDesktop = window.innerWidth > 1000;

    if (_lastFlexHeightVal === safeOffset && _lastIsDesktop === isDesktop)
    {
        return;
    }
    
    _lastFlexHeightVal = safeOffset;
    _lastIsDesktop = isDesktop;
    
    document.documentElement.style.setProperty('--layout-flex-offset', safeOffset + 'px');
}

/*
========================================
 SECTION 5: MAIN LOOP & GLOBAL TRIGGERS
========================================
*/

/**
 * Main animation frame loop controller. 
 * Coordinates layout calculations and applies updates efficiently during scroll events.
 */
function updateLayout()
{
    if (_layoutUpdateRafId !== null)
    {
        return;
    }

    _layoutUpdateRafId = window.requestAnimationFrame(() =>
    {
        const layoutData = calculateMenuPositions();
        
        applyMenuPositions(layoutData, _layoutCache);
        applyOffsetBelow(layoutData.maxBottom, layoutData.headerBottom);

        _layoutUpdateRafId = null;
    });
}

/**
 * Triggers a flex resize update by recalculating menu positions and applying flex sizing.
 * This function is exposed globally for external triggering.
 */
window.triggerFlexResizeUpdate = function()
{
    if (LayoutState.initialized)
    {
        LayoutState.updateHeights();
    }
    const layoutData = calculateMenuPositions();
    applyFlexResize(layoutData.flexOffset);
};

/**
 * Forces the layout engine to rescan the DOM and reapply styles.
 * This should be triggered whenever new HTML elements are dynamically injected into the DOM.
 */
window.triggerDynamicLayoutRefresh = function()
{
    _lastSpacingVal = -1;
    _lastOffsetVal = -1;
    _lastIsDesktop = null;

    if (LayoutState.initialized)
    {
        LayoutState.init(); 

        applySpacing(getAbsoluteHeaderHeight()); 

        updateLayout(); 
    }
};

/*
========================================
 SECTION 6: OBSERVERS & EVENT LISTENERS
========================================
*/

/**
 * Global DOM MutationObserver.
 * Monitors the DOM for dynamically injected elements (e.g., via AJAX, jQuery, Splide).
 * Automatically triggers a layout refresh if newly added nodes match layout-controlled classes.
 */
const LayoutMutationObserver = (function() 
{
    let debounceTimeout = null;

    const triggerClasses = [
        '.adjustPaddingFromTopMenus', '.adjustMarginFromTopMenus',
        '.d-adjustPaddingFromTopMenus', '.d-adjustMarginFromTopMenus',
        '.m-adjustPaddingFromTopMenus', '.m-adjustMarginFromTopMenus',
        '.belowMenu', '.d-belowMenu', '.m-belowMenu',
        '.belowHeader', '.d-belowHeader', '.m-belowHeader',
        '.stickyBelowMenu', '.d-stickyBelowMenu', '.m-stickyBelowMenu',
        '.stickyBelowHeader', '.d-stickyBelowHeader', '.m-stickyBelowHeader',
        '.scrnhAvailable', '.d-scrnhAvailable', '.m-scrnhAvailable'
    ].join(', ');

    const observer = new MutationObserver(function(mutations) 
    {
        let needsRefresh = false;

        for (let i = 0; i < mutations.length; i++) 
        {
            const addedNodes = mutations[i].addedNodes;
            for (let j = 0; j < addedNodes.length; j++) 
            {
                const node = addedNodes[j];
                if (node.nodeType === 1)
                {
                    if (node.matches(triggerClasses) || node.querySelector(triggerClasses)) 
                    {
                        needsRefresh = true;
                        break;
                    }
                }
            }
            if (needsRefresh) break;
        }

        if (needsRefresh) 
        {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(function() 
            {
                if (typeof window.triggerDynamicLayoutRefresh === 'function') 
                {
                    window.triggerDynamicLayoutRefresh();
                }
            }, 50);
        }
    });

    document.addEventListener("DOMContentLoaded", function() 
    {
        observer.observe(document.body, { childList: true, subtree: true });
    });

    return observer;
})();

window.addEventListener('scroll', updateLayout, { passive: true });

window.addEventListener('load', function()
{
    if (LayoutState.initialized)
    {
        LayoutState.updateHeights();
        applySpacing(getAbsoluteHeaderHeight());
    }
    updateLayout();
    window.triggerFlexResizeUpdate();
});

window.addEventListener('resize', function()
{
    LayoutState.updateHeights();
    applySpacing(getAbsoluteHeaderHeight());
    updateLayout();
});

LayoutState.init();
LayoutState.updateHeights();
applySpacing(getAbsoluteHeaderHeight());

/*
==============================
 ADDITIONAL UTILITY FUNCTIONS
==============================
*/

/**
 * Highlights the active anchor in the Lower Menu based on the current scroll position.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.lowerMenu - Container of anchor links
 * @param {string} [options.activeClass="p-on-surface-cl"]
 * @param {string} [options.inactiveClass="d-secondary-cl"]
 */
function updateActiveAnchor({
    lowerMenu = '#LowerMenuContainer',
    activeClass = 'd-secondary-cl',
    inactiveClass = 'p-on-surface-cl'
} = {}) {
    const container = jQuery(lowerMenu)
    const anchors = container.find('.lowerMenuAnchor')

    if (!anchors.length) return

    const scrollPos = jQuery(window).scrollTop()
    let currentId = ''

    jQuery('[data-anchor-name]').each(function ()
    {
        const el = jQuery(this)
        const top = el.offset().top
        const height = el.outerHeight()

        if (scrollPos >= top - height / 3)
        {
            currentId = el.attr('id')
        }
    })

    anchors.each(function()
    {
        const link = jQuery(this)
        const href = link.attr('href') || ''
        const isActive = href === `#${currentId}`

        link.toggleClass(activeClass, isActive)
        link.toggleClass(inactiveClass, !isActive)

        if (isActive)
        {
            if (container[0].scrollWidth > container.innerWidth())
            {
                const centerPos = link.offset().left 
                    - container.offset().left 
                    + container.scrollLeft() 
                    - (container.width() / 2) 
                    + (link.width() / 2)

                container.stop().animate({
                    scrollLeft: centerPos
                }, 200)
            }
        }
    })
}

/**
 * Applies layout and typography classes to rich text content
 *
 * WordPress rich text fields generate HTML without consistent classes.  
 * This function standardizes the output by adding the correct classes  
 * to paragraphs, lists, and headings for visual consistency.
 *
 * @param {Object} args - Function arguments
 * @param {string} args.selector - Selector for the rich text container
 * @param {string} [args.spacing=''] - Optional spacing classes to apply
 */
function applyRichTextLayoutClasses({ selector, spacing = '' })
{
    jQuery(`${selector} p, ${selector} li`)
        .addClass(`tx-sml xs-letter-spc d-on-surface-cl ${spacing}`)

    jQuery(`${selector} ul`)
        .addClass(`cont col fullw ${spacing}`)

    jQuery(selector)
        .find('h1, h2, h3, h4, h5, h6')
        .each(function ()
        {
            const tagName = jQuery(this).prop('tagName').toLowerCase()
            const level = tagName.replace('h', '')

            jQuery(this).addClass(`ttl-${level} ark d-on-surface-cl pt-3 pb-5`)
        })
}

/**
 * Legacy compatibility function for handleEllipsis
 * 
 * This function removes any dot right before an ellipsis,
 * so we don't get an ellipsis with 4 dots or more.
 * 
 * @param {string} elementId - Selector for the element
 * @param {boolean} [updateDataOriginalText=false] - Whether to update 'data-original-text' attribute
 */
function handleEllipsis(elementId, updateDataOriginalText = false)
{
    normalizeEllipsisText({ elementId, updateDataOriginalText });
}

/**
 * Applies custom layout and typography classes to elements within a container
 *
 * Supports full CSS selectors (e.g. 'ul li', 'ol li', 'table td', 'div.highlight').
 * This enables precise styling of nested or specific elements.
 *
 * @param {Object} args - Function arguments
 * @param {string} args.selector - Selector for the container element
 * @param {Object} [args.tags={}] - Object mapping tag selectors to classes (e.g. { 'ul li': 'disc-item' })
 */
function applyCustomLayoutClasses({ selector, tags = {} })
{
    for (const rule in tags)
    {
        if (!tags[rule]) continue

        jQuery(`${selector} ${rule}`)
            .addClass(`${tags[rule]}`)
    }
}

/**
 * Formats the inner HTML of all elements in a node by replacing
 * newline characters with `<br>` tags, except inside <script> and <style>.
 *
 * @param {Object} args - Function arguments
 * @param {jQuery|HTMLElement} args.node - The root node to apply the transformation
 */
function replaceNewlinesWithBreaks({ node })
{
    jQuery(node).find('*:not(script):not(style)').each(function ()
    {
        jQuery(this).html((_, html) =>
        {
            return html.replace(/\n/g, '<br>');
        });
    });
}

/**
 * Normalize text in an element to prevent extra dots before ellipsis
 *
 * Ensures that an ellipsis does not appear with four or more dots
 * by removing any punctuation immediately preceding it.
 *
 * @param {Object} args - Function arguments
 * @param {string|HTMLElement|jQuery} args.elementId - Selector, DOM element, or jQuery element
 * @param {boolean} [args.updateDataOriginalText=false] - Whether to update 'data-original-text' attribute
 */
function normalizeEllipsisText({ elementId, updateDataOriginalText = false })
{
    const element = jQuery(elementId);

    if (element.hasClass('readMoreEnabled'))
    {
        const originalText = element.attr('data-original-text');

        if (originalText)
        {
            element.html(originalText);
        }
    }
    else
    {
        const currentText = element.html();

        if (!element.attr('data-original-text') || updateDataOriginalText)
        {
            element.attr('data-original-text', currentText);
        }

        const modifiedText = currentText.replace(/([.,;!?])(?=\s*(…|\.{3}|<br\s*\/?>|\n|$))/g, '');

        element.html(modifiedText);
    }
}